import React, { useCallback, useMemo, useState } from "react";
import Reveal from "./Reveal";
import projects, { CATEGORIES } from "../../data/projects";
import CaseStudyModal from "./CaseStudyModal";
import FeaturedSlide from "./showcase/FeaturedSlide";
import CarouselControls from "./showcase/CarouselControls";
import ProjectFilterPills from "./showcase/ProjectFilterPills";
import ProjectGrid from "./showcase/ProjectGrid";
import {
  filterByCategory,
  getFeaturedProjects,
} from "../../lib/projectHelpers";
import { trackEvent } from "../../lib/track";

// Featured list is derived from a static import — compute it once at module scope.
const FEATURED = getFeaturedProjects(projects);

const ShowcaseSection = () => {
  const [idx, setIdx] = useState(0);
  const [activeCategory, setActiveCategory] = useState("All");
  const [modalProject, setModalProject] = useState(null);

  const filtered = useMemo(
    () => filterByCategory(projects, activeCategory),
    [activeCategory]
  );

  const next = useCallback(
    () => setIdx((i) => (i + 1) % FEATURED.length),
    []
  );
  const prev = useCallback(
    () => setIdx((i) => (i - 1 + FEATURED.length) % FEATURED.length),
    []
  );
  const openCaseStudy = useCallback((p) => setModalProject(p), []);
  const closeCaseStudy = useCallback(() => setModalProject(null), []);

  // OSIRIS: any "request a similar build" intent enters the system at
  // the Clarity Reset Session. The frame must be named before build work.
  const requestSimilarBuild = useCallback(
    (project) => {
      trackEvent("showcase_request_build_clicked", {
        project: project ? project.name : "generic",
        destination: "orientation_reset_checkout",
      });
      window.dispatchEvent(
        new CustomEvent("am:open-orientation-reset-checkout")
      );
    },
    []
  );

  const current = FEATURED[idx];

  return (
    <section id="showcase" data-testid="showcase-section" className="relative py-28 md:py-40">
      <div className="section-aura" aria-hidden />
      <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
        <Reveal>
          <p className="text-[11px] tracking-[0.5em] uppercase text-gold mb-6 text-center">
            Momentum in Motion · Proof of the Path
          </p>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-themed text-center max-w-3xl mx-auto leading-tight tracking-tight font-light">
            What the work looks like when{" "}
            <span className="italic gold-gradient-text">the frame is named.</span>
          </h2>
          <p
            data-testid="showcase-section-copy"
            className="mt-8 text-themed-muted text-center max-w-2xl mx-auto leading-relaxed"
          >
            Momentum in Motion shows what clarity can become when it is turned into action. These projects are examples of ideas, brands, and systems becoming more structured, visible, and usable.
          </p>
          <p
            data-testid="showcase-section-bridge"
            className="mt-5 text-themed text-center max-w-2xl mx-auto leading-relaxed text-[15.5px] md:text-[16.5px]"
          >
            The Clarity Reset Session is designed to help you find the same kind of starting point in your own life, business, or creative direction.
          </p>
        </Reveal>

        <Reveal delay={100}>
          <FeaturedSlide
            project={current}
            index={idx}
            total={FEATURED.length}
            onCaseStudy={openCaseStudy}
            onScrollToBlueprint={() => requestSimilarBuild(current)}
            carouselControls={
              <CarouselControls
                items={FEATURED}
                activeIndex={idx}
                onSelect={setIdx}
                onPrev={prev}
                onNext={next}
              />
            }
          />
        </Reveal>

        <Reveal delay={150}>
          <ProjectFilterPills
            categories={CATEGORIES}
            active={activeCategory}
            onChange={setActiveCategory}
          />
        </Reveal>

        <Reveal delay={200}>
          <ProjectGrid projects={filtered} onOpen={openCaseStudy} />
        </Reveal>

        <Reveal delay={250}>
          <div className="mt-20 text-center">
            <button
              data-testid="showcase-build-cta"
              onClick={() => requestSimilarBuild(null)}
              className="btn-gold"
            >
              Book a Clarity Reset Session · $149
            </button>
            <p className="mt-3 text-[10px] tracking-[0.3em] uppercase text-themed-muted/70">
              Every build in this gallery started here.
            </p>
          </div>
        </Reveal>
      </div>

      <CaseStudyModal
        project={modalProject}
        open={!!modalProject}
        onClose={closeCaseStudy}
        onRequestBuild={() => {
          requestSimilarBuild(modalProject);
          closeCaseStudy();
        }}
      />
    </section>
  );
};

export default ShowcaseSection;
